import { useCallback, useMemo, useRef } from "react";
import { logDev } from "utils/logs";
import { useFormBuilder } from "../context/FormBuilderContext/context";
import { FieldPreviewProps, OnChangeType } from "../fields/types";
import { DataType, hooks } from "../setup/define";
import { AfterChangePinContent } from "../type";
import useFormState from "./useFormState";

interface Option {
  isReadOnly?: boolean;
  dataType: DataType;
}

export const useFieldState = (itemResultId: string, option: Option) => {
  const {
    pinId,
    formValue,
    value,
    isReadOnly,
    isDisabled,
    inspectionTypeId,
    setValue,
    getField,
  } = useFormState({
    itemResultId,
  });

  const itemResults = useFormBuilder()?.state?.itemResults;
  const formValueRef = useRef(formValue);
  formValueRef.current = formValue;

  const attributes = useMemo(
    () => getField?.(itemResultId) ?? {},
    [itemResultId, getField]
  );

  const afterChange = async (
    params: AfterChangePinContent,
    options?: {
      action: OnChangeType;
    }
  ) => Promise.all(hooks.afterChangeValue.map((cb) => cb(params, options)));

  const onChange = useCallback<NonNullable<FieldPreviewProps["onChange"]>>(
    async (value, options) => {
      if (isReadOnly || isDisabled || option?.isReadOnly) return;
      logDev("Change value", itemResultId, value);

      const isSuccess = await setValue?.(itemResultId, value, option?.dataType);

      if (isSuccess) {
        await afterChange(
          {
            itemResultId,
            itemResults,
            dataType: option?.dataType,
            formValue: formValueRef.current,
            pinId,
            inspectionTypeId,
            data: value,
            saveFieldValue: setValue,
          },
          options
        );
      }

      return isSuccess;
    },
    [
      isReadOnly,
      isDisabled,
      itemResults,
      pinId,
      inspectionTypeId,
      itemResultId,
      option?.dataType,
      option?.isReadOnly,
      setValue,
    ]
  );

  const props = useMemo(() => {
    return {
      isDisabled,
      isReadOnly: isReadOnly || option?.isReadOnly,
      ...attributes,
      value,
      onChange,
    };
  }, [value, attributes, isDisabled, isReadOnly, option.isReadOnly, onChange]);

  return props;
};
